import { clearEdFlowState } from 'features/ed-treatment/edFlowStorage';
import { Check, Stethoscope, Package, Truck, ArrowRight, Mail, CircleAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import { OrderResponse } from 'features/orders/ordersApi';
import { ROUTES } from 'config/route-configuration';

type Summary = {
  drugName: string;
  strengthLabel: string;
  quantityLabel: string;
  intervalLabel: string;
  productImage: string;
  deliveryAddress: string;
};
export type CheckoutReturnPresentationProps = {
  loading: boolean;
  error: string | null;
  isTerminal: boolean;
  isSuccess: boolean;
  order: OrderResponse | null;
  orderId: string | null;
  summary: Summary;
};
// Used both while the route downloads and while the order is being checked.
export function CheckoutReturnLoading({ reference }: { reference?: string | null }) {
  return (
    <section className="checkout-return checkout-return-state" aria-live="polite">
      <div className="return-spinner" aria-hidden="true" />
      <p className="return-eyebrow">One last check</p>
      <h1>Confirming your payment</h1>
      <p className="return-state-copy">
        We’re checking your payment status. Please keep this page open—your confirmation will appear here.
      </p>
      {reference && <p className="return-reference">Order reference: {reference}</p>}
    </section>
  );
}

export default function CheckoutReturnPresentation({
  loading,
  error,
  isTerminal,
  isSuccess,
  order,
  orderId,
  summary,
}: CheckoutReturnPresentationProps) {
  const reference = order?.displayId ?? orderId;
  const amount =
    typeof order?.amountMinor === 'number'
      ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: order.currency ?? 'GBP' }).format(
          order.amountMinor / 100
        )
      : '—';
  if (!error && (loading || !isTerminal)) return <CheckoutReturnLoading reference={reference} />;
  if (error || !isSuccess) {
    return (
      <section className="checkout-return checkout-return-state" aria-live="polite">
        <div className="return-state-icon" aria-hidden="true">
          <CircleAlert size={28} />
        </div>
        <p className="return-eyebrow">Your order</p>
        <h1>{error ? 'We couldn’t load your order' : 'Payment wasn’t completed'}</h1>
        <p className="return-state-copy">
          {error ??
            'Your payment wasn’t completed. Please complete a new assessment to try again, or contact our team for help.'}
        </p>
        {reference && <p className="return-reference">Order reference: {reference}</p>}
        <div className="return-actions">
          <Link
            className="return-primary"
            to={error ? ROUTES.LOGIN : ROUTES.EDQUESTIONNAIRE}
            onClick={error ? undefined : clearEdFlowState}
          >
            {error ? 'Sign in' : 'Start a new assessment'}
            <ArrowRight size={18} />
          </Link>
          <a href="mailto:support@jointalo.com">Contact support</a>
        </div>
      </section>
    );
  }
  return (
    <div className="checkout-return">
      <header className="return-heading">
        <span className="return-success-icon" aria-hidden="true">
          <Check size={30} />
        </span>
        <p className="return-eyebrow">Payment received</p>
        <h1>Thank you for your order</h1>
        <p>Your payment is confirmed. Our clinicians will now review your treatment.</p>
        {reference && <span className="return-reference">Order {reference}</span>}
      </header>
      <div className="return-grid">
        <section className="return-next" aria-labelledby="return-next-heading">
          <h2 id="return-next-heading">What happens next?</h2>
          <ol>
            {[
              {
                icon: Stethoscope,
                title: 'Clinical review',
                text: 'Our clinical team will check your questionnaire to make sure the treatment is safe and suitable for you.',
              },
              {
                icon: Package,
                title: 'Discreet dispatch',
                text: 'If approved, your treatment will be prepared for dispatch. If it isn’t suitable, we’ll be in touch.',
              },
              {
                icon: Truck,
                title: 'Delivered to your door',
                text: 'Your approved treatment will arrive in discreet packaging.',
              },
            ].map(({ icon: Icon, title, text }) => (
              <li key={title}>
                <span className="return-step-icon">
                  <Icon size={22} />
                </span>
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </li>
            ))}
          </ol>
          <Link className="return-primary" to={ROUTES.ACCOUNT}>
            View your account
            <ArrowRight size={18} />
          </Link>
        </section>
        <section className="return-receipt" aria-labelledby="return-receipt-heading">
          <h2 id="return-receipt-heading">Your order</h2>
          <div className="return-product">
            <img src={summary.productImage} alt={`${summary.drugName} tablets`} />
            <div>
              <h3>{summary.drugName}</h3>
              <p>
                {summary.strengthLabel} · {summary.quantityLabel}
              </p>
              <p>{summary.intervalLabel}</p>
            </div>
          </div>
          <div className="return-amount">
            <span>Amount paid</span>
            <strong>{amount}</strong>
          </div>
          <div className="return-delivery">
            <h3>Delivery address</h3>
            <p>{summary.deliveryAddress}</p>
          </div>
          <p className="return-renewal">Your subscription renews {summary.intervalLabel.toLowerCase()}.</p>
        </section>
      </div>
      <footer className="return-help">
        <Mail size={18} />
        <span>
          Questions about your order? <a href="mailto:support@jointalo.com">We’re here to help.</a>
        </span>
      </footer>
    </div>
  );
}
